
/**
 * Module depenencies.
 */

import { CatalogProps } from 'src/types/catalogs';
import { CollectionProps } from 'src/types/collection';
import { MagazineProps } from 'src/types/magazine';
import { breakpoints } from 'src/styles/breakpoints';
import { color, media, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { isEmptyHtml } from 'src/core/utils/strings';
import { isExternalRoute } from 'src/core/utils/routes';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import CatalogCard from 'src/components/cards/catalog-card';
import CollectionCard from 'src/components/cards/collection-card';
import Container from 'src/components/core/layout/container';
import MagazineCard from 'src/components/cards/magazine-card';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import React, { ReactElement, useRef } from 'react';
import Richtext from 'src/components/core/richtext';
import Slider, { Slide } from 'src/components/core/slider';
import SliderControls from 'src/components/core/slider/slider-controls';
import Type from 'src/components/core/typography';
import map from 'lodash/map';
import size from 'lodash/size';
import styled, { css } from 'styled-components';

/**
 * `BaseProps` type.
 */

type BaseProps = {
  className?: string,
  description?: string,
  isKiosk?: boolean,
  linkLabel?: string,
  subtitle?: string,
  title: string,
  url?: string
};

/**
 * `Props` type.
 */

type Props = BaseProps & {
  items: CollectionProps[],
  type: 'collections'
} | BaseProps & {
  items: MagazineProps[],
  type: 'magazines'
} | BaseProps & {
  items: CatalogProps[],
  type: 'catalogs'
};

/**
 * `Section` component.
 */

const Section = styled.section`
  background-color: ${color('white')};
  padding: ${units(13)} 0;
  position: relative;

  ${media.min('ms')`
    padding: ${units(25)} 0;
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 *
 * The gallery starts at the title column and bleeds to the right edge of the
 * page, which is what sets the cards width.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '. title    .'
    '. gallery  gallery'
    '. controls .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme('grid.gutterMobile')}px;

  ${media.min('ms')`
    grid-template-areas:
      '. . title    title    .       .'
      '. . gallery  gallery  gallery gallery'
      '. . controls controls .       .';
    grid-template-columns: ${theme('grid.gutter')}px 1fr 4fr 6fr 1fr ${theme('grid.gutter')}px;
  `}
`;

/**
 * `Header` styled component.
 */

const Header = styled.div`
  grid-area: title;
  margin-bottom: ${units(6)};
`;

/**
 * `Subtitle` styled component.
 *
 * The optional line above the title (`Heading/Tagline` in the 2026 design).
 */

const Subtitle = styled.p`
  color: ${color('brown400')};
  font-size: 16px;
  font-style: italic;
  line-height: 1.5;
  margin-bottom: ${units(2)};
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H3).attrs({ as: 'h2' })`
  color: ${color('black')};
  letter-spacing: -0.01em;
  line-height: 1.2;
  max-width: ${units(73.75)};
`;

/**
 * `Description` styled component.
 */

const Description = styled.div`
  max-width: ${units(73.75)};

  p:last-child {
    margin-bottom: 0;
  }

  p:empty {
    display: none;
  }
`;

/**
 * `Content` styled component.
 *
 * A row with the CTA and the text next to it (the description, or the title
 * when there is none), inside the same parallax layer so the button always
 * moves with that text.
 */

const Content = styled.div<{ noDescription?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${units(3)};
  margin-top: ${ifProp('noDescription', 0, units(2))};

  ${media.min('ms')`
    align-items: flex-end;
    flex-direction: row;
    justify-content: space-between;
  `}

  /*
   * On the title row, lift the CTA from the bottom of the title line box to
   * the text baseline: the H3 half-leading plus descender is ~0.27em, so the
   * wrapper takes only the H3 font size (same fluid range as the h3 type size)
   * to measure it; the button keeps its own font.
   */
  ${ifProp('noDescription', css`
    ${media.min('ms')`
      > :last-child {
        font-size: clamp(32px, 3.889vw, 56px);
        margin-bottom: 0.27em;
      }
    `}
  `)}
`;

/**
 * `Actions` styled component.
 */

const Actions = styled.div`
  display: flex;
  flex-shrink: 0;
  margin-left: auto;
  width: 100%;

  ${media.min('ms')`
    width: auto;
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  border-color: rgba(5, 4, 4, 0.15);
  border-radius: 100px;
  color: ${color('black')};
  height: auto;
  letter-spacing: 0.14px;
  line-height: 1.5;
  min-width: 0;
  padding: ${units(2)} ${units(5)};
  text-align: center;
  width: 100%;

  ${media.min('ms')`
    width: auto;
  `}
`;

/**
 * `Gallery` styled component.
 */

const Gallery = styled.div`
  grid-area: gallery;
  margin-bottom: ${units(6)};
  width: 100%;

  ${media.min('ms')`
    margin-bottom: 0;
  `}
`;

/**
 * `ControlsWrapper` styled component.
 */

const ControlsWrapper = styled.div`
  grid-area: controls;

  ${media.min('ms')`
    padding-top: ${units(3)};
  `}
`;

/**
 * Get carousel config.
 */

function getCarouselConfig() {
  return {
    breakpoints: {
      [breakpoints.sm]: {
        slidesPerView: 1.35,
        spaceBetween: 20
      },
      [breakpoints.ms]: {
        slidesPerView: 2.25,
        spaceBetween: 24
      },
      [breakpoints.md]: {
        slidesPerView: 2.75,
        spaceBetween: 24
      },
      [breakpoints.lg]: {
        slidesPerView: 3.25,
        spaceBetween: 24
      },
      [breakpoints.xl]: {
        slidesPerView: 3.5,
        spaceBetween: 24
      },
      1600: {
        slidesPerView: 4.5,
        spaceBetween: 24
      }
    },
    slidesPerView: 1.35,
    spaceBetween: 20
  };
}

/**
 * `SliderSection` component.
 */

const SliderSection = (props: Props): ReactElement | null => {
  const {
    className,
    description,
    isKiosk,
    items,
    linkLabel,
    subtitle,
    title,
    type,
    url
  } = props;

  const { locale } = useRouter();
  const { translate } = useTranslate();
  const total = size(items);
  const sliderRef = useRef<any>();

  const hasDescription = !isEmptyHtml(description);
  const heading = (
    <>
      {subtitle && (
        <Subtitle>
          {subtitle}
        </Subtitle>
      )}

      <Title>
        {title}
      </Title>
    </>
  );

  // Without a description the CTA sits on the title row, sharing its parallax
  // layer; otherwise it sits on the description row.
  const action = url && (
    <Actions>
      <StyledButton
        {...!isExternalRoute(url) ? { locale } : {
          rel: 'noopener',
          target: '_blank'
        }}
        colorTheme={'primary'}
        href={url}
        size={'medium'}
        variant={'outlined'}
      >
        {linkLabel || translate('common:actions.seeMore')}
      </StyledButton>
    </Actions>
  );

  if (total === 0) {
    return null;
  }

  return (
    <Section className={className}>
      <StyledContainer
        fluid
        hasPadding={!isKiosk}
      >
        <Grid>
          <Header>
            {hasDescription ? (
              <>
                <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                  {heading}
                </ParallaxFadeInUp>

                <ParallaxFadeInUp parallaxOptions={{ speed: 2 }}>
                  <Content>
                    <Description>
                      <Richtext>
                        {description}
                      </Richtext>
                    </Description>

                    {action}
                  </Content>
                </ParallaxFadeInUp>
              </>
            ) : (
              <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                <Content noDescription>
                  <div>
                    {heading}
                  </div>

                  {action}
                </Content>
              </ParallaxFadeInUp>
            )}
          </Header>

          <Gallery>
          <ParallaxFadeInUp
            inViewOptions={{ threshold: 0.1 }}
            parallaxOptions={{ speed: 3 }}
          >
            <Slider
              {...getCarouselConfig()}
              ref={sliderRef}
            >
              {map(items, (item, index: number) => (
                <Slide key={index}>
                  {type === 'collections' && (
                    <CollectionCard
                      {...item}
                      bgColorTheme={'grey'}
                      isKioskCard={isKiosk}
                    />
                  )}

                  {type === 'magazines' && (
                    <MagazineCard
                      {...item}
                      bgColorTheme={'grey'}
                    />
                  )}

                  {type === 'catalogs' && (
                    <CatalogCard
                      {...item}
                      bgColorTheme={'grey'}
                    />
                  )}
                </Slide>
              ))}
            </Slider>
          </ParallaxFadeInUp>
          </Gallery>

          <ControlsWrapper>
            <ParallaxFadeInUp parallaxOptions={{ speed: 3 }}>
              <SliderControls
                sliderRef={sliderRef}
                total={total}
              />
            </ParallaxFadeInUp>
          </ControlsWrapper>
        </Grid>
      </StyledContainer>
    </Section>
  );
};

/**
 * Export `SliderSection` component.
 */

export default SliderSection;
